/**
 * Create/edit form for any reference catalog — the fields are generated from the descriptor, so one
 * component serves all seven. Mirrors Financial/ChartOfAccounts/Components/CreateAccountModal
 * (FormModal shell, local error map, ToastService on the result, onSuccess -> refetch).
 */
import React, { useEffect, useMemo, useState } from 'react';
import FormModal from '@frontend/Components/Modals/FormModal/FormModal';
import ToastService from '@frontend/AppSettings/services/ToastService';
import type { CatalogDescriptor, CatalogField } from './catalogDescriptors';
import {
  CatalogItem,
  createCatalogItem,
  updateCatalogItem,
} from './referenceDataService';
import './BidAdminPage.css';

interface Props {
  catalog: CatalogDescriptor;
  /** null = create, otherwise edit. */
  item: CatalogItem | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /** Options for `catalogRef` fields, keyed by descriptor key. */
  refOptions?: Record<string, Array<{ id: string; label: string }>>;
  /** CREATE-mode initial values (Build U: the Units drawer's "Add unit" pre-fills the
   *  equipment type). Ignored when editing — the item is the truth there. */
  prefill?: Record<string, unknown>;
}

type FormState = Record<string, string | boolean>;

/** API value -> form control value (all controls are strings except checkboxes). */
function toFormValue(field: CatalogField, raw: unknown): string | boolean {
  if (field.type === 'checkbox') return Boolean(raw);
  if (raw === null || raw === undefined) return '';
  if (field.type === 'json') return typeof raw === 'string' ? raw : JSON.stringify(raw, null, 2);
  return String(raw);
}

const CatalogFormModal: React.FC<Props> = ({
  catalog,
  item,
  prefill,
  isOpen,
  onClose,
  onSuccess,
  refOptions = {},
}) => {
  const initial = useMemo<FormState>(() => {
    const state: FormState = {};
    catalog.fields.forEach((f) => {
      state[f.name] = toFormValue(f, item ? item[f.name] : prefill?.[f.name]);
    });
    return state;
  }, [catalog, item, prefill]);

  const [form, setForm] = useState<FormState>(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(initial);
    setErrors({});
  }, [initial]);

  const setField = (name: string, value: string | boolean): void =>
    setForm((prev) => ({ ...prev, [name]: value }));

  /**
   * Form state -> API payload. Blank strings are dropped rather than sent as '': on create that
   * lets the DB defaults apply, and on update the backend's COALESCE treats absent as "no change".
   */
  const buildPayload = (): Record<string, unknown> | null => {
    const next: Record<string, string> = {};
    const payload: Record<string, unknown> = {};

    for (const field of catalog.fields) {
      const value = form[field.name];

      if (field.type === 'checkbox') {
        payload[field.name] = Boolean(value);
        continue;
      }

      const text = String(value ?? '').trim();
      if (!text) {
        if (field.required) {
          next[field.name] = `${field.label} is required`;
        } else if (item && String(toFormValue(field, item[field.name]) ?? '') !== '') {
          // The backend update is COALESCE(%s, col) — an absent value means "no change", so a
          // column cannot be re-nulled through this form. Say so rather than reporting success
          // and leaving the old value in place. (Clearing support would need a backend change.)
          next[field.name] = `${field.label} can't be cleared here — enter a new value instead`;
        }
        continue;
      }

      if (field.type === 'number') {
        const parsed = Number(text);
        if (!Number.isFinite(parsed)) next[field.name] = `${field.label} must be a number`;
        else if (parsed < 0 && !field.allowNegative)
          next[field.name] = `${field.label} cannot be negative`;
        else if (field.integer && !Number.isInteger(parsed))
          next[field.name] = `${field.label} must be a whole number`;
        else payload[field.name] = parsed;
        continue;
      }

      if (field.type === 'json') {
        try {
          payload[field.name] = JSON.parse(text);
        } catch {
          next[field.name] = `${field.label} must be valid JSON`;
        }
        continue;
      }

      payload[field.name] = text;
    }

    setErrors(next);
    return Object.keys(next).length ? null : payload;
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    const payload = buildPayload();
    if (!payload) return;

    setSaving(true);
    try {
      if (item) {
        await updateCatalogItem(catalog.url, item.id, payload);
        ToastService.success(`${catalog.label} item updated`);
      } else {
        await createCatalogItem(catalog.url, payload);
        ToastService.success(`${catalog.label} item created`);
      }
      onSuccess();
      onClose();
    } catch (err: any) {
      ToastService.error(err?.message || 'Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const renderField = (field: CatalogField): JSX.Element => {
    const value = form[field.name];
    const error = errors[field.name];
    const id = `catalog-field-${field.name}`;

    let control: JSX.Element;
    if (field.type === 'checkbox') {
      control = (
        <input
          id={id}
          type="checkbox"
          aria-required={field.required}
          checked={Boolean(value)}
          onChange={(e) => setField(field.name, e.target.checked)}
        />
      );
    } else if (field.type === 'textarea' || field.type === 'json') {
      control = (
        <textarea
          id={id}
          className="bid-admin-input"
          aria-required={field.required}
          rows={field.type === 'json' ? 4 : 2}
          value={String(value ?? '')}
          placeholder={field.placeholder}
          onChange={(e) => setField(field.name, e.target.value)}
        />
      );
    } else if (field.type === 'catalogRef') {
      control = (
        <select
          id={id}
          className="bid-admin-input"
          aria-required={field.required}
          value={String(value ?? '')}
          onChange={(e) => setField(field.name, e.target.value)}
        >
          <option value="">— none —</option>
          {(refOptions[field.refCatalog ?? ''] ?? []).map((opt) => (
            <option key={opt.id} value={opt.id}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    } else {
      control = (
        <input
          id={id}
          className="bid-admin-input"
          aria-required={field.required}
          type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
          value={String(value ?? '')}
          placeholder={field.placeholder}
          onChange={(e) => setField(field.name, e.target.value)}
        />
      );
    }

    return (
      <div className={`bid-admin-field ${field.type === 'checkbox' ? 'is-check' : ''}`} key={field.name}>
        <label htmlFor={id}>
          {field.label}
          {/* aria-hidden so the accessible name stays "Name", not "Name *"; the required state is
              carried by aria-required on the control itself. */}
          {field.required && (
            <span className="bid-admin-required" aria-hidden="true">
              {' '}
              *
            </span>
          )}
        </label>
        {control}
        {error && (
          <span className="bid-admin-field-error" role="alert">
            {error}
          </span>
        )}
      </div>
    );
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={`${item ? 'Edit' : 'New'} ${catalog.label} item`}
      size="large"
    >
      <form onSubmit={handleSubmit} className="bid-admin-form">
        <div className="bid-admin-fields">{catalog.fields.map(renderField)}</div>
        <div className="bid-admin-form-actions">
          <button type="button" className="bid-admin-btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="bid-admin-btn is-primary" disabled={saving}>
            {saving ? 'Saving…' : item ? 'Save changes' : 'Create'}
          </button>
        </div>
      </form>
    </FormModal>
  );
};

export default CatalogFormModal;
